import { ComponentProps, useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import { ThemeProvider } from "@emotion/react";
import { darkTheme, lightTheme } from "styles/theme";

export function MixThemeProvider(
  props: Omit<ComponentProps<typeof ThemeProvider>, "theme">,
) {
  const { resolvedTheme, systemTheme, setTheme } = useTheme();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const previousThemeRef = useRef<string | null>(null);

  useEffect(() => setMounted(true), []);

  const isPaymentsPage = router.pathname.startsWith("/payments/");
  const isPaymentsFormPage = router.asPath.startsWith("/payments/form/");
  const shouldForceLightTheme = isPaymentsPage || isPaymentsFormPage;

  // Ensure the document theme is also light when forcing, then restore
  useEffect(() => {
    if (!mounted) return;
    if (shouldForceLightTheme) {
      if (resolvedTheme === "dark") {
        previousThemeRef.current = "dark";
      }
      setTheme("light");
    } else if (previousThemeRef.current) {
      setTheme(previousThemeRef.current);
      previousThemeRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldForceLightTheme, mounted]);

  // During SSR and before hydration, use light theme as default
  // This prevents hydration mismatch
  if (!mounted) {
    return <ThemeProvider theme={lightTheme} {...props} />;
  }

  const theme = shouldForceLightTheme
    ? lightTheme
    : resolvedTheme === "dark"
    ? darkTheme
    : resolvedTheme === "light"
    ? lightTheme
    : systemTheme === "dark"
    ? darkTheme
    : lightTheme;

  return <ThemeProvider theme={theme} {...props} />;
}
